import React from "react";
import { Button, SectionContainer } from "@silversea-ssc/ui-components-react";
import {
  BrochureFragment,
  BrochureGroupingFragment,
  FormDisclaimerFragment,
  ImageFragment
} from "types/graphql-types";
import BrochurePageBox from "components/BrochurePageBox/BrochurePageBox";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { isPresent } from "src/utils/checks";
import {
  useBrochureThankYouPageURL,
  useTargetBrochures
} from "src/utils/brochures";
import { Language } from "src/utils/localization/languages/types";
import { usePageType } from "components/PageBuilder/usePageType";

const NewRequestBrochureModal = React.lazy(
  () => import("components/RequestBrochureModal/RequestBrochureModal")
);

interface TravelGuideProps {
  sectionTitle?: string;
  image?: ImageFragment;
  guideTitle?: string;
  content?: string;
  brochureGroup?: BrochureGroupingFragment;
  ctaText?: string;
}

const TravelGuide: React.FC<React.PropsWithChildren<TravelGuideProps>> = ({
  brochureGroup,
  guideTitle,
  sectionTitle,
  content,
  image,
  ctaText
}) => {
  const geolocation = useGeolocation();
  const language = useLanguage();
  const brochures = brochureGroup?.brochures?.filter(isPresent);
  const filteredBrochures =
    useTargetBrochures(brochures, geolocation, language) ?? [];
  const isSingleBrochure = filteredBrochures.length === 1;

  return (
    <div id="travel_guide" data-content-name="travel_guide">
      <SectionContainer
        backgroundColor="titanium"
        spacingTop="regular"
        spacingBottom="regular"
      >
        {isSingleBrochure ? (
          <BrochurePageBoxToModal
            sectionTitle={sectionTitle}
            image={image}
            guideTitle={guideTitle}
            content={content}
            ctaText={ctaText}
            brochure={filteredBrochures[0] as BrochureFragment}
            language={language}
            formDisclaimerContent={
              brochureGroup?.formDisclaimerContent as FormDisclaimerFragment
            }
          />
        ) : (
          <BrochurePageBoxToBrochurePage
            fullPath={brochureGroup?.fullPath}
            sectionTitle={sectionTitle}
            image={image}
            guideTitle={guideTitle}
            content={content}
            ctaText={ctaText}
          />
        )}
      </SectionContainer>
    </div>
  );
};

export default TravelGuide;

const BrochurePageBoxToBrochurePage: React.FC<
  React.PropsWithChildren<
    Omit<TravelGuideProps, "brochureGroup"> & { fullPath: string | undefined }
  >
> = ({ sectionTitle, image, guideTitle, content, ctaText, fullPath }) => {
  const ctaButton = (
    <Button
      variant="primary"
      dataContentPiece="view_the_brochures"
      href={fullPath}
    >
      {ctaText}
    </Button>
  );
  return (
    <BrochurePageBox
      ctaButton={ctaButton}
      backgroundColor={"white"}
      titleColor={"titanium"}
      sectionTitle={sectionTitle}
      title={guideTitle}
      content={content}
      image={image}
    />
  );
};

const BrochurePageBoxToModal: React.FC<
  React.PropsWithChildren<
    Omit<TravelGuideProps, "brochureGroup"> & {
      brochure: BrochureFragment;
      language: Language;
      formDisclaimerContent: FormDisclaimerFragment | undefined;
    }
  >
> = ({
  sectionTitle,
  image,
  guideTitle,
  content,
  ctaText,
  brochure,
  language,
  formDisclaimerContent
}) => {
  const [showModal, setShowModal] = React.useState(false);

  const pageType = usePageType();
  const thankYouPageURL = useBrochureThankYouPageURL(language, pageType);

  const ctaButton = (
    <Button
      variant="primary"
      dataContentPiece="request_brochure"
      onClick={() => setShowModal(true)}
    >
      {ctaText}
    </Button>
  );
  return (
    <>
      <BrochurePageBox
        ctaButton={ctaButton}
        backgroundColor={"white"}
        titleColor={"titanium"}
        sectionTitle={sectionTitle}
        title={guideTitle}
        content={content}
        image={image}
      />
      {showModal && thankYouPageURL && (
        <React.Suspense>
          <NewRequestBrochureModal
            isOpen={true}
            onRequestClose={() => setShowModal(false)}
            brochure={brochure}
            thankYouStragegy={"redirect"}
            thankYouPageURL={thankYouPageURL}
            formDisclaimerContent={formDisclaimerContent}
          />
        </React.Suspense>
      )}
    </>
  );
};
